import { useCallback, useEffect, useRef } from "react";
import { useUnit } from "effector-react";
import { button, folder, useControls } from "leva";
import type { Schema } from "leva/dist/declarations/src/types/public.js";
import type { CameraConfig } from "../../cameraConfig";
import type { CollisionConfig } from "../../scene/collision/colliderTypes";
import type {
  DevConfig,
  SparkRenderSettingsConfig,
  ResponsiveRenderSettingsConfig,
  TieredRenderSettingsConfig,
  SplatEncodingConfig,
} from "../../devConfig";
import type { DeviceClass, DeviceTier } from "../../scene/qualityProfile";
import type { PoiConfig } from "../../poiConfig";
import type { AudioConfig } from "../../state/appConfig";
import type { TellerAssistantConfig } from "../../tellerAssistantConfig";
import { MUSIC_TRACKS } from "../../audio/musicTracks";
import {
  $appConfig,
  $audioConfig,
  $backgroundConfig,
  $cameraConfig,
  $collisionConfig,
  $collidersConfig,
  $devConfig,
  $poiConfig,
  $previewModelConfig,
  $tellerAssistantConfig,
  resetAppConfig,
  updateAudioConfig,
  updateBackgroundConfig,
  updateCameraConfig,
  updateCollisionConfig,
  updateSingleCollider,
  updateColliders,
  updateDevConfig,
  updatePoiConfig,
  updatePreviewModelConfig,
  type BackgroundConfigState,
  type PreviewModelConfig,
  updateTellerAssistantConfig,
} from "../../state/appConfig";
import type { ColliderDef, BoxColliderDef, CylinderColliderDef, SphereColliderDef } from "../../scene/collision/colliderTypes";
import { getSplatSceneConfig } from "../../scene/splatRegistry";
import { $gizmoTargetId, setGizmoTarget } from "../../state/appConfig";
import { $viewMode } from "../../state/viewMode";
import { $locale, setLocale } from "../../state/locale";
import { $sceneBindings, $sceneId } from "../../state/appState";
import { useDevMarkers } from "../../hooks/useDevMarkers";

type NumberControlMetadata = {
  type: "number";
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  hint?: string;
};

type SelectControlMetadata = {
  type: "select";
  options: Record<string, string>;
  label?: string;
  hint?: string;
};

type StringControlMetadata = {
  type: "string";
  label?: string;
  hint?: string;
};

type BooleanControlMetadata = {
  type?: "boolean";
  label?: string;
  hint?: string;
};

type ControlMetadata =
  | NumberControlMetadata
  | BooleanControlMetadata
  | SelectControlMetadata
  | StringControlMetadata;
type ConfigKey<T> = Extract<keyof T, string>;
type ConfigMetadata<T extends object> = Partial<
  Record<ConfigKey<T>, ControlMetadata>
>;

const CAMERA_METADATA = {
  fov: {
    type: "number",
    label: "Field of View",
    min: 10,
    max: 120,
    step: 1,
  },
  lookSpeedHorizontal: {
    type: "number",
    label: "Look Speed X",
    min: 1,
    max: 360,
    step: 1,
  },
  lookSpeedVertical: {
    type: "number",
    label: "Look Speed Y",
    min: 1,
    max: 360,
    step: 1,
  },
  dragInertia: {
    type: "number",
    label: "Drag Inertia",
    min: 0,
    max: 0.999,
    step: 0.001,
  },
  movementSpeed: {
    type: "number",
    label: "Move Speed",
    min: 0,
    max: 32,
    step: 0.05,
  },
  orbitAutoMove: {
    type: "boolean",
    label: "Auto Orbit",
    hint: "Continuously orbit around the target while in orbit view",
  },
  orbitAutoMoveDirection: {
    type: "select",
    label: "Orbit Direction",
    options: {
      Left: "left",
      Right: "right",
    },
  },
  orbitAutoMoveSpeed: {
    type: "number",
    label: "Orbit Speed",
    hint: "Angular speed (radians/sec) when auto-orbit is enabled",
    min: 0,
    max: 5,
    step: 0.01,
  },
  orbitMinDistance: {
    type: "number",
    label: "Orbit Min Dist",
    hint: "Closest camera distance allowed in orbit view",
    min: 0,
    max: 50,
    step: 0.01,
  },
  orbitMaxDistance: {
    type: "number",
    label: "Orbit Max Dist",
    hint: "Farthest camera distance allowed in orbit view",
    min: 0,
    max: 50,
    step: 0.01,
  },
  orbitMinPolarAngle: {
    type: "number",
    label: "Orbit Min Polar",
    hint: "Minimum vertical angle (radians) for orbit",
    min: 0,
    max: Math.PI,
    step: 0.01,
  },
  orbitMaxPolarAngle: {
    type: "number",
    label: "Orbit Max Polar",
    hint: "Maximum vertical angle (radians) for orbit",
    min: 0,
    max: Math.PI,
    step: 0.01,
  },
  heightCoef: {
    type: "number",
    label: "Height Mult",
    min: 0,
    max: 10,
    step: 0.1,
  },
  speedHeightMin: {
    type: "number",
    label: "Min Height Spd",
    min: -2,
    max: 2,
    step: 0.01,
  },
  speedHeightMax: {
    type: "number",
    label: "Max Height Spd",
    min: -2,
    max: 2,
    step: 0.01,
  },
  pinchMoveSpeed: {
    type: "number",
    label: "Pinch Speed",
    min: 0,
    max: 0.05,
    step: 0.0005,
  },
  verticalMin: {
    type: "number",
    label: "Vertical Min",
    min: 0,
    max: Math.PI,
    step: 0.01,
  },
  verticalMax: {
    type: "number",
    label: "Vertical Max",
    min: 0,
    max: Math.PI,
    step: 0.01,
  },
  clampMinHeight: {
    type: "number",
    label: "Clamp Min H",
    min: -10,
    max: 10,
    step: 0.01,
  },
  clampMaxHeight: {
    type: "number",
    label: "Clamp Max H",
    min: -10,
    max: 100,
    step: 0.01,
  },
  clampMaxHeightFar: {
    type: "number",
    label: "Clamp Max H (Far)",
    hint: "Maximum camera Y at the farthest orbit zoom distance",
    min: -10,
    max: 100,
    step: 0.01,
  },
} satisfies ConfigMetadata<CameraConfig>;

const COLLISION_METADATA = {
  cameraRadius: {
    type: "number",
    label: "Camera Radius",
    min: 0,
    max: 0.5,
    step: 0.001,
  },
  maxDistance: {
    type: "number",
    label: "Raycast Range",
    min: 0.1,
    max: 10,
    step: 0.1,
  },
  epsilon: {
    type: "number",
    label: "Epsilon",
    min: 0,
    max: 0.01,
    step: 0.0001,
  },
} satisfies ConfigMetadata<CollisionConfig>;

const DEV_METADATA = {
  showReferenceGrid: {
    type: "boolean",
    label: "Reference Grid",
    hint: "Toggle the floor/ceiling guides that follow the clamp heights",
  },
  showCollisionBounds: {
    type: "boolean",
    label: "Collision Bounds",
    hint: "Outline all collision helpers (green AABBs + hidden FBX volumes)",
  },
  showSightMarkers: {
    type: "boolean",
    label: "Sight Markers",
    hint: "Display magenta spheres for every sight anchor in the scene",
  },
  showOrbitCenterMarker: {
    type: "boolean",
    label: "Orbit Center Marker",
    hint: "Render a helper sphere at the orbit pivot to aid debugging",
  },
  showTellerAssistant: {
    type: "boolean",
    label: "Teller Assistant",
    hint: "Render the teller assistant companion model",
  },
  showRearView: {
    type: "boolean",
    label: "Rear View",
    hint: "Display a rear-facing Spark render overlay on the camera",
  },
  skipPreloaderAnimation: {
    type: "boolean",
    label: "Skip Preloader Animation",
    hint: "Skip preview-tour orbital fly-around + camera flyover (splat reveal still runs). Toggle mid-session collapses cinematic instantly.",
  },
  keepPreviewModel: {
    type: "boolean",
    label: "Keep Preview Model",
    hint: "Keep the preview point-cloud visible; hide splat mesh",
  },
  debugSplatTransform: {
    type: "boolean",
    label: "Debug Splat Transform",
    hint: "Show both preview GLB and splat; enable transform sliders",
  },
  debugBboxes: {
    type: "boolean",
    label: "Debug Bboxes",
    hint: "Visualize preview-GLB (teal) and splat (magenta=centers, orange=full) bboxes + axes",
  },
  previewLoadDelaySec: {
    type: "number",
    label: "Preview Load Delay (s)",
    hint: "Artificial delay before preview model is considered loaded (for testing loading animations). Up to 5 minutes.",
    min: 0,
    max: 300,
    step: 0.5,
  },
  assistantApiToken: {
    type: "string",
    label: "Assistant API Token",
    hint: "Bearer token for assistant requests (optional)",
  },
  mockPoiArtifactButtons: {
    type: "boolean",
    label: "Mock POI Artifacts",
    hint: "[TEMP] Show enter-poi-artifact buttons in chat suggestions",
  },
  disableForcedViewMode: {
    type: "boolean",
    label: "Disable Forced View Mode",
    hint: "Ignore controls.forcedViewMode from scene config — FPS scenes (Mina, kaaba-inside) fall back to orbit camera for tuning. Requires reload.",
  },
  unlockCameraDuringPreloader: {
    type: "boolean",
    label: "Unlock Camera In Preloader",
    hint: "Allow free camera control during preloader animation (preview-tour orbit + flyover skipped). Reveal-shader still plays. Requires reload.",
  },
  unlockFpsMovement: {
    type: "boolean",
    label: "Unlock FPS Movement",
    hint: "Enable WASD/arrows/R/F/pinch-zoom translation in forced-FPS scenes (Mina, kaaba-inside). Useful for navigating interiors during tuning. Requires reload.",
  },
  unlockCameraConstraints: {
    type: "boolean",
    label: "Unlock Camera Constraints",
    hint: "Drop orbit polar/azimuth/distance/cage/groundPlane locks at runtime — free-fly camera for parameter tuning. Y-clamp not affected.",
  },
} satisfies ConfigMetadata<DevConfig>;

const RENDER_SETTINGS_METADATA = {
  blurAmount: { type: "number", label: "Blur Amount", min: 0, max: 1, step: 0.01 },
  preBlurAmount: { type: "number", label: "Pre-Blur", min: 0, max: 1, step: 0.01 },
  maxStdDev: { type: "number", label: "Max Std Dev", min: 0.5, max: 10, step: 0.1 },
  pixelRatio: { type: "number", label: "Pixel Ratio Cap", min: 0.25, max: 3, step: 0.05 },
  minPixelRadius: { type: "number", label: "Min Pixel Radius", min: 0, max: 10, step: 0.5 },
  maxPixelRadius: { type: "number", label: "Max Pixel Radius", min: 64, max: 9999, step: 1 },
  autoUpdate: { type: "boolean", label: "Auto Update" },
  sortRadial: { type: "boolean", label: "Sort Radial" },
  sort32: { type: "boolean", label: "Sort 32-bit" },
  sortDistance: { type: "number", label: "Sort Distance", min: 0.001, max: 1, step: 0.001 },
  sortCoorient: { type: "boolean", label: "Sort Coorient" },
  depthBias: { type: "number", label: "Depth Bias", min: 0, max: 10, step: 0.1 },
  sort360: { type: "boolean", label: "Sort 360" },
  stochastic: { type: "boolean", label: "Stochastic" },
  premultipliedAlpha: { type: "boolean", label: "Premultiplied Alpha" },
  preUpdate: { type: "boolean", label: "Pre-Update" },
  originDistance: { type: "number", label: "Origin Distance", min: 0.1, max: 10, step: 0.1 },
  minAlpha: { type: "number", label: "Min Alpha", min: 0, max: 0.1, step: 0.001 },
  enable2DGS: { type: "boolean", label: "Enable 2DGS" },
  focalDistance: { type: "number", label: "DoF Focal Dist", min: 0, max: 100, step: 0.1 },
  apertureAngle: { type: "number", label: "DoF Aperture", min: 0, max: Math.PI, step: 0.01 },
  falloff: { type: "number", label: "Falloff", min: 0, max: 1, step: 0.01 },
  clipXY: { type: "number", label: "Clip XY", min: 1, max: 3, step: 0.1 },
  focalAdjustment: { type: "number", label: "Focal Adjustment", min: 0.1, max: 4, step: 0.1 },
} satisfies ConfigMetadata<SparkRenderSettingsConfig>;

const SPLAT_ENCODING_METADATA = {
  rgbMin: { type: "number", label: "RGB Min", min: -2, max: 0, step: 0.1, hint: "Lower bound for RGB color encoding range" },
  rgbMax: { type: "number", label: "RGB Max", min: 1, max: 4, step: 0.1, hint: "Upper bound for RGB color encoding range" },
  lnScaleMin: { type: "number", label: "Ln Scale Min", min: -20, max: 0, step: 1, hint: "Lower bound for log-scale encoding" },
  lnScaleMax: { type: "number", label: "Ln Scale Max", min: 0, max: 20, step: 1, hint: "Upper bound for log-scale encoding" },
  sh1Min: { type: "number", label: "SH1 Min", min: -4, max: 0, step: 0.1, hint: "Lower bound for SH band 1 coefficients" },
  sh1Max: { type: "number", label: "SH1 Max", min: 0, max: 4, step: 0.1, hint: "Upper bound for SH band 1 coefficients" },
  sh2Min: { type: "number", label: "SH2 Min", min: -4, max: 0, step: 0.1, hint: "Lower bound for SH band 2 coefficients" },
  sh2Max: { type: "number", label: "SH2 Max", min: 0, max: 4, step: 0.1, hint: "Upper bound for SH band 2 coefficients" },
  sh3Min: { type: "number", label: "SH3 Min", min: -4, max: 0, step: 0.1, hint: "Lower bound for SH band 3 coefficients" },
  sh3Max: { type: "number", label: "SH3 Max", min: 0, max: 4, step: 0.1, hint: "Upper bound for SH band 3 coefficients" },
} satisfies ConfigMetadata<SplatEncodingConfig>;

const BLUR_PRESETS = {
  aa: { blurAmount: 0.3, preBlurAmount: 0 },
  "non-aa": { blurAmount: 0, preBlurAmount: 0.3 },
} as const;

type BlurPreset = "aa" | "non-aa" | "custom";
const BLUR_PRESET_CONTROL_KEY = "00 Blur Preset";

const detectBlurPreset = (
  blurAmount: number,
  preBlurAmount: number
): BlurPreset => {
  if (
    blurAmount === BLUR_PRESETS.aa.blurAmount &&
    preBlurAmount === BLUR_PRESETS.aa.preBlurAmount
  ) {
    return "aa";
  }
  if (
    blurAmount === BLUR_PRESETS["non-aa"].blurAmount &&
    preBlurAmount === BLUR_PRESETS["non-aa"].preBlurAmount
  ) {
    return "non-aa";
  }
  return "custom";
};

const RENDER_SETTINGS_PRIMARY_KEYS = [
  "maxStdDev",
  "pixelRatio",
  "minPixelRadius",
  "maxPixelRadius",
  "autoUpdate",
] as const satisfies readonly (keyof SparkRenderSettingsConfig)[];

const RENDER_SETTINGS_SORTING_KEYS = [
  "sortRadial",
  "sort32",
  "sortDistance",
  "sortCoorient",
  "depthBias",
  "sort360",
  "stochastic",
] as const satisfies readonly (keyof SparkRenderSettingsConfig)[];

const RENDER_SETTINGS_ADVANCED_KEYS = [
  "premultipliedAlpha",
  "preUpdate",
  "originDistance",
  "minAlpha",
  "enable2DGS",
  "focalDistance",
  "apertureAngle",
  "falloff",
  "clipXY",
  "focalAdjustment",
] as const satisfies readonly (keyof SparkRenderSettingsConfig)[];

const POI_METADATA = {
  showVisibleOnly: {
    type: "boolean",
    label: "Hide Occluded",
    hint: "Only render POI markers if their sight is visible from the camera",
  },
  occlusionProbeMaxOffset: {
    type: "number",
    label: "Probe Offset Max",
    hint: "Maximum distance to pull the occlusion probe toward the camera",
    min: 0,
    max: 1,
    step: 0.01,
  },
  occlusionProbeMinDistance: {
    type: "number",
    label: "Probe Distance Min",
    hint: "Closest allowed distance between the probe and the POI center",
    min: 0,
    max: 0.5,
    step: 0.001,
  },
} satisfies ConfigMetadata<PoiConfig>;

const AUDIO_METADATA = {
  soundOn: {
    type: "boolean",
    label: "Sound On",
  },
  musicOn: {
    type: "boolean",
    label: "Music On",
  },
  musicTrackId: {
    type: "select",
    label: "Music Track",
    options: Object.fromEntries(
      MUSIC_TRACKS.map((track) => [track.label, track.id])
    ),
  },
  musicVolume: {
    type: "number",
    label: "Music Volume",
    min: 0,
    max: 1,
    step: 0.01,
  },
} satisfies ConfigMetadata<AudioConfig>;

const LOCALE_OPTIONS = {
  English: "en",
  Arabic: "ar",
} as const;

const SKYBOX_TEXTURE_PRESETS: Record<string, string> = {
  "autumn_field_puresky": "/environment/autumn_field_puresky.jpg",
  "kloofendal_48d_partly_cloudy_puresky": "/environment/kloofendal_48d_partly_cloudy_puresky.jpg",
  "andrey_skybox": "/environment/andrey_skybox.exr",
  "Custom URL": "__custom__",
};

const detectSkyboxPreset = (url: string): string => {
  if (!url) return "";
  const entry = Object.entries(SKYBOX_TEXTURE_PRESETS).find(([, v]) => v === url);
  return entry ? entry[1] : "__custom__";
};

const formatLabel = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^\w/, (match) => match.toUpperCase());

const isNumberMetadata = (
  metadata?: ControlMetadata
): metadata is NumberControlMetadata => metadata?.type === "number";

const isSelectMetadata = (
  metadata?: ControlMetadata
): metadata is SelectControlMetadata => metadata?.type === "select";

const isStringMetadata = (
  metadata?: ControlMetadata
): metadata is StringControlMetadata => metadata?.type === "string";

type ChangeContext = {
  initial?: boolean;
};

const createSectionSchema = (
  values: object,
  metadata: Partial<Record<string, ControlMetadata>>,
  onChange: (key: string, value: number | boolean | string) => void
): Schema => {
  const schema: Schema = {};

  for (const [key, rawValue] of Object.entries(values)) {
    const controlMetadata = metadata[key];
    const label = controlMetadata?.label ?? formatLabel(key);
    const hint = controlMetadata?.hint;

    if (typeof rawValue === "number") {
      schema[key] = {
        value: rawValue,
        label,
        hint,
        min: isNumberMetadata(controlMetadata)
          ? controlMetadata.min
          : undefined,
        max: isNumberMetadata(controlMetadata)
          ? controlMetadata.max
          : undefined,
        step: isNumberMetadata(controlMetadata)
          ? controlMetadata.step
          : undefined,
        onChange: (
          nextValue: number,
          _path: string,
          context: ChangeContext
        ) => {
          if (context.initial) {
            return;
          }
          onChange(key, nextValue);
        },
      };
    } else if (typeof rawValue === "boolean") {
      schema[key] = {
        value: rawValue,
        label,
        hint,
        onChange: (
          nextValue: boolean,
          _path: string,
          context: ChangeContext
        ) => {
          if (context.initial) {
            return;
          }
          onChange(key, nextValue);
        },
      };
    } else if (typeof rawValue === "string") {
      if (isSelectMetadata(controlMetadata)) {
        schema[key] = {
          value: rawValue,
          label,
          hint,
          options: controlMetadata.options,
          onChange: (
            nextValue: string,
            _path: string,
            context: ChangeContext
          ) => {
            if (context.initial) {
              return;
            }
            onChange(key, nextValue);
          },
        };
      } else if (isStringMetadata(controlMetadata)) {
        schema[key] = {
          value: rawValue,
          label,
          hint,
          onChange: (
            nextValue: string,
            _path: string,
            context: ChangeContext
          ) => {
            if (context.initial) {
              return;
            }
            onChange(key, nextValue);
          },
        };
      }
    }
  }

  return schema;
};

const pickObjectKeys = <T extends object, K extends keyof T>(
  values: T,
  keys: readonly K[]
): Pick<T, K> => {
  const picked = {} as Pick<T, K>;
  for (const key of keys) {
    picked[key] = values[key];
  }
  return picked;
};

type RenderSettingsPanelSet = (value: Record<string, unknown>) => void;
type RenderProfileKey = `${DeviceClass}.${DeviceTier}`;

const createRenderSettingsSchema = (
  values: SparkRenderSettingsConfig,
  profile: RenderProfileKey,
  onSettingChange: (
    profile: RenderProfileKey,
    key: keyof SparkRenderSettingsConfig,
    value: number | boolean | string
  ) => void,
  onBlurPresetChange: (
    profile: RenderProfileKey,
    preset: BlurPreset
  ) => void,
  setPanelValues: RenderSettingsPanelSet,
  shouldMarkPresetAsCustom: (
    profile: RenderProfileKey
  ) => boolean
): Schema => ({
  [BLUR_PRESET_CONTROL_KEY]: {
    label: "Blur Preset",
    value: detectBlurPreset(values.blurAmount, values.preBlurAmount),
    options: {
      AA: "aa",
      "Non-AA": "non-aa",
      Custom: "custom",
    },
    onChange: (
      nextValue: BlurPreset,
      _path: string,
      context: ChangeContext
    ) => {
      if (context.initial) {
        return;
      }
      onBlurPresetChange(profile, nextValue);
    },
  },
  blurAmount: {
    value: values.blurAmount,
    label: RENDER_SETTINGS_METADATA.blurAmount.label,
    min: RENDER_SETTINGS_METADATA.blurAmount.min,
    max: RENDER_SETTINGS_METADATA.blurAmount.max,
    step: RENDER_SETTINGS_METADATA.blurAmount.step,
    onChange: (
      nextValue: number,
      _path: string,
      context: ChangeContext
    ) => {
      if (context.initial) {
        return;
      }
      onSettingChange(profile, "blurAmount", nextValue);
      if (shouldMarkPresetAsCustom(profile)) {
        setPanelValues({
          [BLUR_PRESET_CONTROL_KEY]: "custom",
        });
      }
    },
  },
  preBlurAmount: {
    value: values.preBlurAmount,
    label: RENDER_SETTINGS_METADATA.preBlurAmount.label,
    min: RENDER_SETTINGS_METADATA.preBlurAmount.min,
    max: RENDER_SETTINGS_METADATA.preBlurAmount.max,
    step: RENDER_SETTINGS_METADATA.preBlurAmount.step,
    onChange: (
      nextValue: number,
      _path: string,
      context: ChangeContext
    ) => {
      if (context.initial) {
        return;
      }
      onSettingChange(profile, "preBlurAmount", nextValue);
      if (shouldMarkPresetAsCustom(profile)) {
        setPanelValues({
          [BLUR_PRESET_CONTROL_KEY]: "custom",
        });
      }
    },
  },
  ...createSectionSchema(
    pickObjectKeys(values, RENDER_SETTINGS_PRIMARY_KEYS),
    RENDER_SETTINGS_METADATA,
    (key, value) =>
      onSettingChange(profile, key as keyof SparkRenderSettingsConfig, value)
  ),
  Sorting: folder(
    createSectionSchema(
      pickObjectKeys(values, RENDER_SETTINGS_SORTING_KEYS),
      RENDER_SETTINGS_METADATA,
      (key, value) =>
        onSettingChange(profile, key as keyof SparkRenderSettingsConfig, value)
    ),
    { collapsed: true }
  ),
  Advanced: folder(
    createSectionSchema(
      pickObjectKeys(values, RENDER_SETTINGS_ADVANCED_KEYS),
      RENDER_SETTINGS_METADATA,
      (key, value) =>
        onSettingChange(profile, key as keyof SparkRenderSettingsConfig, value)
    ),
    { collapsed: true }
  ),
});

export function AppConfigPanel() {
  const { appConfig, cameraConfig, collisionConfig, collidersConfig, devConfig, poiConfig, audioConfig, viewMode, locale, previewModelConfig, backgroundConfig, tellerAssistantConfig, gizmoTargetId, sceneBindings, sceneId } = useUnit({
    appConfig: $appConfig,
    cameraConfig: $cameraConfig,
    collisionConfig: $collisionConfig,
    collidersConfig: $collidersConfig,
    devConfig: $devConfig,
    poiConfig: $poiConfig,
    audioConfig: $audioConfig,
    tellerAssistantConfig: $tellerAssistantConfig,
    viewMode: $viewMode,
    locale: $locale,
    previewModelConfig: $previewModelConfig,
    backgroundConfig: $backgroundConfig,
    gizmoTargetId: $gizmoTargetId,
    sceneBindings: $sceneBindings,
    sceneId: $sceneId,
  });

  const { buildSchema: buildMarkerSchema, schemaDeps: markerSchemaDeps, captureSet: captureMarkerSet } = useDevMarkers({
    scene: sceneBindings?.scene ?? null,
    camera: sceneBindings?.camera ?? null,
    controls: sceneBindings?.controls ?? null,
    renderer: sceneBindings?.renderer ?? null,
  });
  const [
    pushAudioConfig,
    pushCameraConfig,
    pushCollisionConfig,
    pushSingleCollider,
    pushColliders,
    pushGizmoTarget,
    pushDevConfig,
    pushPoiConfig,
    pushTellerAssistantConfig,
    resetConfig,
    changeLocale,
    pushPreviewModelConfig,
    pushBackgroundConfig,
  ] =
    useUnit([
      updateAudioConfig,
      updateCameraConfig,
      updateCollisionConfig,
      updateSingleCollider,
      updateColliders,
      setGizmoTarget,
      updateDevConfig,
      updatePoiConfig,
      updateTellerAssistantConfig,
      resetAppConfig,
      setLocale,
      updatePreviewModelConfig,
      updateBackgroundConfig,
  ]);

  const renderSetRefs = useRef<Record<RenderProfileKey, RenderSettingsPanelSet>>({
    'desktop.low': () => undefined,
    'desktop.middle': () => undefined,
    'desktop.high': () => undefined,
    'mobile.low': () => undefined,
    'mobile.middle': () => undefined,
    'mobile.high': () => undefined,
  });
  const blurPresetSyncLockRef = useRef<Record<RenderProfileKey, boolean>>({
    'desktop.low': false,
    'desktop.middle': false,
    'desktop.high': false,
    'mobile.low': false,
    'mobile.middle': false,
    'mobile.high': false,
  });

  const handleCameraChange = useCallback(
    (key: string, value: number | boolean | string) => {
      const nextConfig = {
        ...cameraConfig,
        [key]: value,
      };
      pushCameraConfig(nextConfig);
    },
    [cameraConfig, pushCameraConfig]
  );

  const handleOrbitCenterChange = useCallback(
    (axis: 'x' | 'y' | 'z', value: number) => {
      const nextConfig = {
        ...cameraConfig,
        orbitCenter: {
          ...cameraConfig.orbitCenter,
          [axis]: value,
        },
      };
      pushCameraConfig(nextConfig);
    },
    [cameraConfig, pushCameraConfig]
  );

  const handleViewOffsetChange = useCallback(
    (key: keyof CameraConfig['viewOffset'], value: number | boolean) => {
      const nextConfig = {
        ...cameraConfig,
        viewOffset: {
          ...cameraConfig.viewOffset,
          [key]: value,
        },
      };
      pushCameraConfig(nextConfig);
    },
    [cameraConfig, pushCameraConfig]
  );

  const handleCollisionChange = useCallback(
    (key: string, value: number | boolean | string) => {
      if (typeof value === "string") {
        return;
      }
      const nextConfig = {
        ...collisionConfig,
        [key]: value,
      };
      pushCollisionConfig(nextConfig);
    },
    [collisionConfig, pushCollisionConfig]
  );

  const handleColliderFieldChange = useCallback(
    (id: string, patch: Partial<ColliderDef>) => {
      pushSingleCollider({ id, patch });
    },
    [pushSingleCollider]
  );

  const handleCopyColliderConfig = useCallback(() => {
    const payload = JSON.stringify(collidersConfig, null, 2);
    console.log("Collider Config", collidersConfig);
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(payload);
    }
  }, [collidersConfig]);

  const handleResetColliders = useCallback(() => {
    const sceneConfig = sceneId != null ? getSplatSceneConfig(String(sceneId)) : undefined;
    const defaultColliders = sceneConfig?.colliders ?? [];
    pushColliders(structuredClone(defaultColliders));
  }, [pushColliders, sceneId]);

  const handleAddCollider = useCallback(
    (type: 'box' | 'box-hollow' | 'sphere' | 'cylinder') => {
      const camera = sceneBindings?.camera;
      const pos = camera
        ? { x: +camera.position.x.toFixed(3), y: +camera.position.y.toFixed(3), z: +camera.position.z.toFixed(3) }
        : { x: 0, y: 0, z: 0 };

      const id = `${type}-${Date.now()}`;

      let newCollider: ColliderDef;
      if (type === 'box') {
        newCollider = {
          id,
          type: 'box',
          mode: 'solid' as const,
          enabled: true,
          position: pos,
          size: { x: 0.3, y: 0.3, z: 0.3 },
        };
      } else if (type === 'box-hollow') {
        // Hollow box — закрытое помещение (стены/пол/потолок). Камера
        // остаётся ВНУТРИ объёма. Дефолт-центр кладём в текущий orbitTarget
        // (если есть), чтобы сразу обнять то, что юзер смотрит.
        const orbitTarget = sceneBindings?.controls.orbit.target;
        const boxPos = orbitTarget
          ? { x: +orbitTarget.x.toFixed(3), y: +orbitTarget.y.toFixed(3), z: +orbitTarget.z.toFixed(3) }
          : pos;
        newCollider = {
          id,
          type: 'box',
          mode: 'hollow' as const,
          enabled: true,
          blocksLineOfSight: false,
          position: boxPos,
          size: { x: 4, y: 4, z: 4 },
        };
      } else if (type === 'sphere') {
        newCollider = {
          id,
          type: 'sphere',
          mode: 'solid' as const,
          enabled: true,
          position: pos,
          radius: 0.15,
        };
      } else {
        const orbitTarget = sceneBindings?.controls.orbit.target;
        const cylPos = orbitTarget
          ? { x: +orbitTarget.x.toFixed(3), y: +orbitTarget.y.toFixed(3), z: +orbitTarget.z.toFixed(3) }
          : pos;
        newCollider = {
          id,
          type: 'cylinder',
          mode: 'hollow' as const,
          enabled: true,
          blocksLineOfSight: false,
          position: cylPos,
          radius: 3.0,
          height: 2.0,
        };
      }

      pushColliders([...collidersConfig, newCollider]);

      if (!devConfig.showCollisionBounds) {
        pushDevConfig({ ...devConfig, showCollisionBounds: true });
      }
    },
    [sceneBindings, collidersConfig, pushColliders, devConfig, pushDevConfig],
  );

  const handleDeleteCollider = useCallback(
    (id: string) => {
      if (gizmoTargetId === id) {
        pushGizmoTarget(null);
      }
      pushColliders(collidersConfig.filter((c) => c.id !== id));
    },
    [collidersConfig, pushColliders, gizmoTargetId, pushGizmoTarget],
  );

  const handleDevChange = useCallback(
    (key: string, value: number | boolean | string) => {
      const nextConfig = {
        ...devConfig,
        [key]: value,
      };
      pushDevConfig(nextConfig);
    },
    [devConfig, pushDevConfig]
  );

  const parseProfileKey = (profile: RenderProfileKey): [DeviceClass, DeviceTier] => {
    const [deviceClass, tier] = profile.split('.') as [DeviceClass, DeviceTier];
    return [deviceClass, tier];
  };

  const handleRenderSettingsChange = useCallback(
    (
      profile: RenderProfileKey,
      key: keyof SparkRenderSettingsConfig,
      value: number | boolean | string
    ) => {
      if (typeof value === "string") {
        return;
      }
      const [deviceClass, tier] = parseProfileKey(profile);
      const nextConfig = {
        ...devConfig,
        renderSettings: {
          ...devConfig.renderSettings,
          [deviceClass]: {
            ...devConfig.renderSettings[deviceClass],
            [tier]: {
              ...devConfig.renderSettings[deviceClass][tier],
              [key]: value,
            },
          },
        },
      };
      pushDevConfig(nextConfig);
    },
    [devConfig, pushDevConfig]
  );

  const handleSplatEncodingChange = useCallback(
    (key: string, value: number | boolean | string) => {
      if (typeof value !== "number") {
        return;
      }
      const nextConfig = {
        ...devConfig,
        splatEncoding: {
          ...devConfig.splatEncoding,
          [key]: value,
        },
      };
      pushDevConfig(nextConfig);
    },
    [devConfig, pushDevConfig]
  );

  const handleBlurPresetChange = useCallback(
    (
      profile: RenderProfileKey,
      preset: BlurPreset
    ) => {
      if (preset === "custom") {
        return;
      }
      const [deviceClass, tier] = parseProfileKey(profile);
      const presetValues = BLUR_PRESETS[preset];
      blurPresetSyncLockRef.current[profile] = true;
      const setPanelValues = renderSetRefs.current[profile];
      setPanelValues({
        [BLUR_PRESET_CONTROL_KEY]: preset,
        blurAmount: presetValues.blurAmount,
        preBlurAmount: presetValues.preBlurAmount,
      });
      const nextConfig = {
        ...devConfig,
        renderSettings: {
          ...devConfig.renderSettings,
          [deviceClass]: {
            ...devConfig.renderSettings[deviceClass],
            [tier]: {
              ...devConfig.renderSettings[deviceClass][tier],
              blurAmount: presetValues.blurAmount,
              preBlurAmount: presetValues.preBlurAmount,
            },
          },
        },
      };
      pushDevConfig(nextConfig);
      queueMicrotask(() => {
        blurPresetSyncLockRef.current[profile] = false;
      });
    },
    [devConfig, pushDevConfig]
  );

  const shouldMarkBlurPresetAsCustom = useCallback(
    (profile: RenderProfileKey): boolean =>
      !blurPresetSyncLockRef.current[profile],
    []
  );

  const handleCopyAppConfig = useCallback(() => {
    const snapshot = {
      ...appConfig,
      renderSettings: appConfig.dev.renderSettings,
      previewModel: previewModelConfig,
      background: backgroundConfig,
      viewMode,
      locale,
    };
    const payload = JSON.stringify(snapshot, null, 2);
    console.log("Copy App Config", snapshot);
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(payload);
    }
  }, [appConfig, backgroundConfig, locale, previewModelConfig, viewMode]);

  const handlePoiChange = useCallback(
    (key: string, value: number | boolean | string) => {
      if (typeof value === "string") {
        return;
      }
      const nextConfig = {
        ...poiConfig,
        [key]: value,
      };
      pushPoiConfig(nextConfig);
    },
    [poiConfig, pushPoiConfig]
  );

  const handleAudioChange = useCallback(
    (key: string, value: number | boolean | string) => {
      const nextConfig = {
        ...audioConfig,
        [key]: value,
      };
      pushAudioConfig(nextConfig);
    },
    [audioConfig, pushAudioConfig]
  );

  const handlePreviewWavesChange = useCallback(
    (key: keyof PreviewModelConfig, value: number) => {
      pushPreviewModelConfig({ [key]: value });
    },
    [pushPreviewModelConfig]
  );

  const handleWavesCenterChange = useCallback(
    (axis: 'x' | 'y' | 'z', value: number) => {
      pushPreviewModelConfig({
        wavesCenter: {
          ...previewModelConfig.wavesCenter,
          [axis]: value,
        },
      });
    },
    [previewModelConfig.wavesCenter, pushPreviewModelConfig]
  );

  const handleBackgroundChange = useCallback(
    (key: keyof BackgroundConfigState, value: number | boolean | string | [number, number, number]) => {
      pushBackgroundConfig({ [key]: value });
    },
    [pushBackgroundConfig]
  );

  const handleSkyboxRotationChange = useCallback(
    (axis: 0 | 1 | 2, value: number) => {
      const newRotation: [number, number, number] = [...backgroundConfig.skyboxRotation];
      newRotation[axis] = value;
      pushBackgroundConfig({ skyboxRotation: newRotation });
    },
    [backgroundConfig.skyboxRotation, pushBackgroundConfig]
  );
  const handleTellerAssistantTransformChange = useCallback(
    (key: 'position' | 'rotationY' | 'scale' | 'hoverAmplitude', value: number | { x: number; y: number; z: number }) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        transform: {
          ...tellerAssistantConfig.transform,
          [key]: value,
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantPositionChange = useCallback(
    (axis: 'x' | 'y' | 'z', value: number) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        transform: {
          ...tellerAssistantConfig.transform,
          position: {
            ...tellerAssistantConfig.transform.position,
            [axis]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantDesktopChange = useCallback(
    (key: 'scale' | 'hoverAmplitude' | 'paddingRight' | 'verticalOffset', value: number) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        transform: {
          ...tellerAssistantConfig.transform,
          desktop: {
            ...tellerAssistantConfig.transform.desktop,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantMobileChange = useCallback(
    (key: 'scale' | 'hoverAmplitude' | 'paddingRight' | 'verticalOffset', value: number) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        transform: {
          ...tellerAssistantConfig.transform,
          mobile: {
            ...tellerAssistantConfig.transform.mobile,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantMeshChange = useCallback(
    (key: string, value: number | boolean | string) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        mesh: {
          ...tellerAssistantConfig.mesh,
          [key]: value,
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantShellChange = useCallback(
    (key: string, value: number | string) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        shell: {
          ...tellerAssistantConfig.shell,
          [key]: value,
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantLightingTypeChange = useCallback(
    (lightingType: 'simple' | 'env') => {
      const nextConfig = {
        ...tellerAssistantConfig,
        lighting: {
          ...tellerAssistantConfig.lighting,
          lightingType,
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantSimpleLightChange = useCallback(
    (axis: 'lightX' | 'lightY' | 'lightZ', value: number) => {
      const key = axis === 'lightX' ? 'lightX' : axis === 'lightY' ? 'lightY' : 'lightZ';
      const nextConfig = {
        ...tellerAssistantConfig,
        lighting: {
          ...tellerAssistantConfig.lighting,
          simple: {
            ...tellerAssistantConfig.lighting.simple,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantSimpleLightColorChange = useCallback(
    (key: 'ambientLightColor' | 'directionalLightColor', value: string) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        lighting: {
          ...tellerAssistantConfig.lighting,
          simple: {
            ...tellerAssistantConfig.lighting.simple,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantSimpleLightIntensityChange = useCallback(
    (key: 'ambientLightIntensity' | 'directionalLightIntensity', value: number) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        lighting: {
          ...tellerAssistantConfig.lighting,
          simple: {
            ...tellerAssistantConfig.lighting.simple,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantEnvLightChange = useCallback(
    (key: keyof TellerAssistantConfig['lighting']['env'], value: number) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        lighting: {
          ...tellerAssistantConfig.lighting,
          env: {
            ...tellerAssistantConfig.lighting.env,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );


  useControls(
    "Localization",
    () => ({
      Language: {
        value: locale,
        options: LOCALE_OPTIONS,
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          changeLocale(nextValue as typeof locale);
        },
      },
    }),
    [changeLocale, locale]
  );

  useControls(
    "Camera",
    () =>
      createSectionSchema(cameraConfig, CAMERA_METADATA, handleCameraChange),
    [cameraConfig, handleCameraChange]
  );

  const isOrbitCenterProgrammatic = useRef(false);
  const orbitCenterLiveRef = useRef({ x: 0, y: 0, z: 0 });

  const [, setOrbitCenter] = useControls(
    "Orbit Center",
    () => ({
      "Center X": {
        value: cameraConfig.orbitCenter.x,
        min: -5,
        max: 5,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial || isOrbitCenterProgrammatic.current) return;
          handleOrbitCenterChange('x', nextValue);
        },
      },
      "Center Y": {
        value: cameraConfig.orbitCenter.y,
        min: -5,
        max: 5,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial || isOrbitCenterProgrammatic.current) return;
          handleOrbitCenterChange('y', nextValue);
        },
      },
      "Center Z": {
        value: cameraConfig.orbitCenter.z,
        min: -5,
        max: 5,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial || isOrbitCenterProgrammatic.current) return;
          handleOrbitCenterChange('z', nextValue);
        },
      },
      "Copy Center": button(() => {
        const { x, y, z } = orbitCenterLiveRef.current;
        const payload = `{ x: ${x}, y: ${y}, z: ${z} }`;
        navigator.clipboard?.writeText(payload).catch(() => {});
        // eslint-disable-next-line no-console
        console.log("[Orbit Center] Copied:", payload);
      }),
    }),
    [cameraConfig, handleOrbitCenterChange]
  );

  // Sync live orbit.target → Leva sliders (rAF loop, like GlbDevPanel Camera Debug)
  useEffect(() => {
    if (!sceneBindings) return;
    const prev = { x: 0, y: 0, z: 0 };
    let rafId: number;

    const tick = () => {
      const t = sceneBindings.controls.orbit.target;
      const x = +t.x.toFixed(2);
      const y = +t.y.toFixed(2);
      const z = +t.z.toFixed(2);

      if (x !== prev.x || y !== prev.y || z !== prev.z) {
        isOrbitCenterProgrammatic.current = true;
        setOrbitCenter({ "Center X": x, "Center Y": y, "Center Z": z });
        isOrbitCenterProgrammatic.current = false;
        prev.x = x;
        prev.y = y;
        prev.z = z;
        orbitCenterLiveRef.current = { x, y, z };
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [sceneBindings, setOrbitCenter]);

  // Live camera position: read-only display + copy button
  const cameraPositionLiveRef = useRef({ x: 0, y: 0, z: 0 });
  const isCameraPositionProgrammatic = useRef(false);

  const [, setCameraPosition] = useControls(
    "Camera Position",
    () => ({
      "Pos X": {
        value: 0,
        step: 0.01,
        disabled: true,
        onChange: (_v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isCameraPositionProgrammatic.current) return;
        },
      },
      "Pos Y": {
        value: 0,
        step: 0.01,
        disabled: true,
        onChange: (_v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isCameraPositionProgrammatic.current) return;
        },
      },
      "Pos Z": {
        value: 0,
        step: 0.01,
        disabled: true,
        onChange: (_v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isCameraPositionProgrammatic.current) return;
        },
      },
      "Copy Position": button(() => {
        const { x, y, z } = cameraPositionLiveRef.current;
        const payload = `{ x: ${x}, y: ${y}, z: ${z} }`;
        navigator.clipboard?.writeText(payload).catch(() => {});
        // eslint-disable-next-line no-console
        console.log("[Camera Position] Copied:", payload);
      }),
    }),
    []
  );

  // Sync live camera.position → Leva sliders
  useEffect(() => {
    if (!sceneBindings) return;
    const prev = { x: NaN, y: NaN, z: NaN };
    let rafId: number;

    const tick = () => {
      const p = sceneBindings.camera.position;
      const x = +p.x.toFixed(2);
      const y = +p.y.toFixed(2);
      const z = +p.z.toFixed(2);

      if (x !== prev.x || y !== prev.y || z !== prev.z) {
        isCameraPositionProgrammatic.current = true;
        setCameraPosition({ "Pos X": x, "Pos Y": y, "Pos Z": z });
        isCameraPositionProgrammatic.current = false;
        prev.x = x;
        prev.y = y;
        prev.z = z;
        cameraPositionLiveRef.current = { x, y, z };
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [sceneBindings, setCameraPosition]);

  useControls(
    "View Offset",
    () => ({
      Enabled: {
        value: cameraConfig.viewOffset.enabled,
        onChange: (nextValue: boolean, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleViewOffsetChange('enabled', nextValue);
        },
      },
      Top: {
        value: cameraConfig.viewOffset.top,
        min: 0,
        max: 2000,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleViewOffsetChange('top', nextValue);
        },
      },
      Right: {
        value: cameraConfig.viewOffset.right,
        min: 0,
        max: 2000,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleViewOffsetChange('right', nextValue);
        },
      },
      Bottom: {
        value: cameraConfig.viewOffset.bottom,
        min: 0,
        max: 2000,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleViewOffsetChange('bottom', nextValue);
        },
      },
      Left: {
        value: cameraConfig.viewOffset.left,
        min: 0,
        max: 2000,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleViewOffsetChange('left', nextValue);
        },
      },
    }),
    [cameraConfig.viewOffset, handleViewOffsetChange]
  );

  useControls(
    "Collision",
    () =>
      createSectionSchema(
        collisionConfig,
        COLLISION_METADATA,
        handleCollisionChange
      ),
    [collisionConfig, handleCollisionChange]
  );

  useControls(
    "Colliders",
    () => {
      const schema: Schema = {};

      schema["Add Box"] = button(() => handleAddCollider('box'));
      schema["Add Box (Hollow)"] = button(() => handleAddCollider('box-hollow'));
      schema["Add Sphere"] = button(() => handleAddCollider('sphere'));
      schema["Add Cylinder (Hollow)"] = button(() => handleAddCollider('cylinder'));

      for (const collider of collidersConfig) {
        const colFolder: Schema = {
          [`${collider.id}-enabled`]: {
            value: collider.enabled,
            label: "Enabled",
            onChange: (v: boolean, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              handleColliderFieldChange(collider.id, { enabled: v });
            },
          },
          [`${collider.id}-posX`]: {
            value: collider.position.x,
            label: "Position X",
            min: -50, max: 50, step: 0.01,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              handleColliderFieldChange(collider.id, { position: { ...collider.position, x: v } });
            },
          },
          [`${collider.id}-posY`]: {
            value: collider.position.y,
            label: "Position Y",
            min: -50, max: 50, step: 0.01,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              handleColliderFieldChange(collider.id, { position: { ...collider.position, y: v } });
            },
          },
          [`${collider.id}-posZ`]: {
            value: collider.position.z,
            label: "Position Z",
            min: -50, max: 50, step: 0.01,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              handleColliderFieldChange(collider.id, { position: { ...collider.position, z: v } });
            },
          },
        };

        if (collider.type === 'box') {
          const box = collider as BoxColliderDef;
          colFolder[`${collider.id}-sizeX`] = {
            value: box.size.x,
            label: "Size X",
            min: 0.01, max: 50, step: 0.01,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              handleColliderFieldChange(collider.id, { size: { ...box.size, x: v } } as Partial<BoxColliderDef>);
            },
          };
          colFolder[`${collider.id}-sizeY`] = {
            value: box.size.y,
            label: "Size Y",
            min: 0.01, max: 50, step: 0.01,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              handleColliderFieldChange(collider.id, { size: { ...box.size, y: v } } as Partial<BoxColliderDef>);
            },
          };
          colFolder[`${collider.id}-sizeZ`] = {
            value: box.size.z,
            label: "Size Z",
            min: 0.01, max: 50, step: 0.01,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              handleColliderFieldChange(collider.id, { size: { ...box.size, z: v } } as Partial<BoxColliderDef>);
            },
          };

          // Rotation displayed in degrees in the UI; stored in config as Euler radians (XYZ order).
          const RAD_TO_DEG = 180 / Math.PI;
          const DEG_TO_RAD = Math.PI / 180;
          const currentRotation = box.rotation ?? { x: 0, y: 0, z: 0 };
          colFolder[`${collider.id}-rotX`] = {
            value: currentRotation.x * RAD_TO_DEG,
            label: "Rot X (°)",
            min: -180, max: 360, step: 1,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              const next = box.rotation ?? { x: 0, y: 0, z: 0 };
              handleColliderFieldChange(collider.id, {
                rotation: { x: v * DEG_TO_RAD, y: next.y, z: next.z },
              } as Partial<BoxColliderDef>);
            },
          };
          colFolder[`${collider.id}-rotY`] = {
            value: currentRotation.y * RAD_TO_DEG,
            label: "Rot Y (°)",
            min: -180, max: 360, step: 1,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              const next = box.rotation ?? { x: 0, y: 0, z: 0 };
              handleColliderFieldChange(collider.id, {
                rotation: { x: next.x, y: v * DEG_TO_RAD, z: next.z },
              } as Partial<BoxColliderDef>);
            },
          };
          colFolder[`${collider.id}-rotZ`] = {
            value: currentRotation.z * RAD_TO_DEG,
            label: "Rot Z (°)",
            min: -180, max: 360, step: 1,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              const next = box.rotation ?? { x: 0, y: 0, z: 0 };
              handleColliderFieldChange(collider.id, {
                rotation: { x: next.x, y: next.y, z: v * DEG_TO_RAD },
              } as Partial<BoxColliderDef>);
            },
          };
        } else if (collider.type === 'sphere') {
          const sph = collider as SphereColliderDef;
          colFolder[`${collider.id}-radius`] = {
            value: sph.radius,
            label: "Radius",
            min: 0.01, max: 50, step: 0.01,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              handleColliderFieldChange(collider.id, { radius: v } as Partial<SphereColliderDef>);
            },
          };
        } else {
          const cyl = collider as CylinderColliderDef;
          colFolder[`${collider.id}-radius`] = {
            value: cyl.radius,
            label: "Radius",
            min: 0.01, max: 50, step: 0.01,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              handleColliderFieldChange(collider.id, { radius: v } as Partial<CylinderColliderDef>);
            },
          };
          colFolder[`${collider.id}-height`] = {
            value: cyl.height,
            label: "Height",
            min: 0.01, max: 50, step: 0.01,
            onChange: (v: number, _p: string, ctx: ChangeContext) => {
              if (ctx.initial) return;
              handleColliderFieldChange(collider.id, { height: v } as Partial<CylinderColliderDef>);
            },
          };
        }

        colFolder["Copy Params"] = button(() => {
          let params: Record<string, unknown>;
          if (collider.type === 'box') {
            const box = collider as BoxColliderDef;
            params = box.rotation
              ? { position: box.position, size: box.size, rotation: box.rotation }
              : { position: box.position, size: box.size };
          } else if (collider.type === 'sphere') {
            params = { position: collider.position, radius: (collider as SphereColliderDef).radius };
          } else {
            params = { position: collider.position, radius: (collider as CylinderColliderDef).radius, height: (collider as CylinderColliderDef).height };
          }
          const text = JSON.stringify(params, null, 2);
          navigator.clipboard?.writeText(text);
        });

        const gizmoLabel = gizmoTargetId === collider.id ? "Detach Gizmo" : "Attach Gizmo";
        colFolder[gizmoLabel] = button(() => {
          pushGizmoTarget(gizmoTargetId === collider.id ? null : collider.id);
        });

        colFolder["Delete"] = button(() => handleDeleteCollider(collider.id));

        const typeLabel = collider.type === 'box' ? 'Box' : collider.type === 'sphere' ? 'Sphere' : 'Cylinder';
        schema[`${collider.id} (${typeLabel})`] = folder(colFolder, { collapsed: false });
      }

      schema["Copy All Colliders"] = button(() => handleCopyColliderConfig());
      schema["Reset to Defaults"] = button(() => handleResetColliders());

      return schema;
    },
    [collidersConfig, handleColliderFieldChange, handleCopyColliderConfig, handleResetColliders, handleAddCollider, handleDeleteCollider, gizmoTargetId, pushGizmoTarget]
  );

  useControls(
    "Dev Tools",
    () => createSectionSchema(devConfig, DEV_METADATA, handleDevChange),
    [devConfig, handleDevChange]
  );

  const desktopHighControls = useControls(
    "Render Settings / Desktop High",
    () => createRenderSettingsSchema(
      devConfig.renderSettings.desktop.high,
      "desktop.high",
      handleRenderSettingsChange,
      handleBlurPresetChange,
      (v) => renderSetRefs.current['desktop.high'](v),
      shouldMarkBlurPresetAsCustom
    ),
    [devConfig.renderSettings.desktop.high, handleRenderSettingsChange, handleBlurPresetChange, shouldMarkBlurPresetAsCustom]
  );
  renderSetRefs.current['desktop.high'] = desktopHighControls[1] as RenderSettingsPanelSet;

  const desktopMiddleControls = useControls(
    "Render Settings / Desktop Middle",
    () => createRenderSettingsSchema(
      devConfig.renderSettings.desktop.middle,
      "desktop.middle",
      handleRenderSettingsChange,
      handleBlurPresetChange,
      (v) => renderSetRefs.current['desktop.middle'](v),
      shouldMarkBlurPresetAsCustom
    ),
    [devConfig.renderSettings.desktop.middle, handleRenderSettingsChange, handleBlurPresetChange, shouldMarkBlurPresetAsCustom]
  );
  renderSetRefs.current['desktop.middle'] = desktopMiddleControls[1] as RenderSettingsPanelSet;

  const desktopLowControls = useControls(
    "Render Settings / Desktop Low",
    () => createRenderSettingsSchema(
      devConfig.renderSettings.desktop.low,
      "desktop.low",
      handleRenderSettingsChange,
      handleBlurPresetChange,
      (v) => renderSetRefs.current['desktop.low'](v),
      shouldMarkBlurPresetAsCustom
    ),
    [devConfig.renderSettings.desktop.low, handleRenderSettingsChange, handleBlurPresetChange, shouldMarkBlurPresetAsCustom]
  );
  renderSetRefs.current['desktop.low'] = desktopLowControls[1] as RenderSettingsPanelSet;

  const mobileHighControls = useControls(
    "Render Settings / Mobile High",
    () => createRenderSettingsSchema(
      devConfig.renderSettings.mobile.high,
      "mobile.high",
      handleRenderSettingsChange,
      handleBlurPresetChange,
      (v) => renderSetRefs.current['mobile.high'](v),
      shouldMarkBlurPresetAsCustom
    ),
    [devConfig.renderSettings.mobile.high, handleRenderSettingsChange, handleBlurPresetChange, shouldMarkBlurPresetAsCustom]
  );
  renderSetRefs.current['mobile.high'] = mobileHighControls[1] as RenderSettingsPanelSet;

  const mobileMiddleControls = useControls(
    "Render Settings / Mobile Middle",
    () => createRenderSettingsSchema(
      devConfig.renderSettings.mobile.middle,
      "mobile.middle",
      handleRenderSettingsChange,
      handleBlurPresetChange,
      (v) => renderSetRefs.current['mobile.middle'](v),
      shouldMarkBlurPresetAsCustom
    ),
    [devConfig.renderSettings.mobile.middle, handleRenderSettingsChange, handleBlurPresetChange, shouldMarkBlurPresetAsCustom]
  );
  renderSetRefs.current['mobile.middle'] = mobileMiddleControls[1] as RenderSettingsPanelSet;

  const mobileLowControls = useControls(
    "Render Settings / Mobile Low",
    () => createRenderSettingsSchema(
      devConfig.renderSettings.mobile.low,
      "mobile.low",
      handleRenderSettingsChange,
      handleBlurPresetChange,
      (v) => renderSetRefs.current['mobile.low'](v),
      shouldMarkBlurPresetAsCustom
    ),
    [devConfig.renderSettings.mobile.low, handleRenderSettingsChange, handleBlurPresetChange, shouldMarkBlurPresetAsCustom]
  );
  renderSetRefs.current['mobile.low'] = mobileLowControls[1] as RenderSettingsPanelSet;

  useControls(
    "Splat Encoding",
    () => ({
      "⚠️ Reload required": {
        value: "Changes take effect after page reload",
        editable: false,
      },
      ...createSectionSchema(
        devConfig.splatEncoding,
        SPLAT_ENCODING_METADATA,
        handleSplatEncodingChange
      ),
    }),
    [devConfig.splatEncoding, handleSplatEncodingChange]
  );

  useControls(
    "Points of Interest",
    () => createSectionSchema(poiConfig, POI_METADATA, handlePoiChange),
    [poiConfig, handlePoiChange]
  );

  useControls(
    "Audio",
    () => createSectionSchema(audioConfig, AUDIO_METADATA, handleAudioChange),
    [audioConfig, handleAudioChange]
  );

  const skyboxTextureSetRef = useRef<((value: Record<string, unknown>) => void)>(() => undefined);
  const skyboxTextureSyncLockRef = useRef(false);

  const bgControls = useControls(
    "Background",
    () => ({
      Color: {
        value: '#' + backgroundConfig.color.toString(16).padStart(6, '0'),
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          const colorNum = parseInt(nextValue.replace('#', ''), 16);
          handleBackgroundChange('color', colorNum);
        },
      },
      Skybox: {
        value: backgroundConfig.skyboxEnabled,
        onChange: (nextValue: boolean, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleBackgroundChange('skyboxEnabled', nextValue);
        },
      },
      "Skybox Texture": {
        value: detectSkyboxPreset(backgroundConfig.skyboxUrl),
        options: SKYBOX_TEXTURE_PRESETS,
        label: "Texture Preset",
        hint: "Choose a preset skybox texture or select Custom URL to enter your own",
        render: (get: (path: string) => boolean) => get('Background.Skybox'),
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          if (nextValue === "__custom__") return;
          skyboxTextureSyncLockRef.current = true;
          skyboxTextureSetRef.current({ "Skybox URL": nextValue });
          handleBackgroundChange('skyboxUrl', nextValue);
          queueMicrotask(() => { skyboxTextureSyncLockRef.current = false; });
        },
      },
      "Skybox URL": {
        value: backgroundConfig.skyboxUrl,
        label: "Skybox URL",
        hint: "Absolute URL for the skybox texture (equirectangular image)",
        render: (get: (path: string) => boolean) => get('Background.Skybox'),
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleBackgroundChange('skyboxUrl', nextValue);
          if (!skyboxTextureSyncLockRef.current) {
            const preset = detectSkyboxPreset(nextValue);
            skyboxTextureSetRef.current({ "Skybox Texture": preset });
          }
        },
      },
      Intensity: {
        value: backgroundConfig.skyboxIntensity,
        min: 0,
        max: 1,
        step: 0.01,
        render: (get) => get('Background.Skybox'),
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleBackgroundChange('skyboxIntensity', nextValue);
        },
      },
      Blur: {
        value: backgroundConfig.skyboxBlur,
        min: 0,
        max: 1,
        step: 0.01,
        render: (get) => get('Background.Skybox'),
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleBackgroundChange('skyboxBlur', nextValue);
        },
      },
      "Rotation X": {
        value: backgroundConfig.skyboxRotation[0],
        min: 0,
        max: 360,
        step: 1,
        render: (get) => get('Background.Skybox'),
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleSkyboxRotationChange(0, nextValue);
        },
      },
      "Rotation Y": {
        value: backgroundConfig.skyboxRotation[1],
        min: 0,
        max: 360,
        step: 1,
        render: (get) => get('Background.Skybox'),
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleSkyboxRotationChange(1, nextValue);
        },
      },
      "Rotation Z": {
        value: backgroundConfig.skyboxRotation[2],
        min: 0,
        max: 360,
        step: 1,
        render: (get) => get('Background.Skybox'),
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleSkyboxRotationChange(2, nextValue);
        },
      },
    }),
    [backgroundConfig, handleBackgroundChange, handleSkyboxRotationChange]
  );
  skyboxTextureSetRef.current = bgControls[1] as (value: Record<string, unknown>) => void;

  useControls(
    "Preview Waves",
    () => ({
      "Center X": {
        value: previewModelConfig.wavesCenter.x,
        min: -10,
        max: 10,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleWavesCenterChange('x', nextValue);
        },
      },
      "Center Y": {
        value: previewModelConfig.wavesCenter.y,
        min: -10,
        max: 10,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleWavesCenterChange('y', nextValue);
        },
      },
      "Center Z": {
        value: previewModelConfig.wavesCenter.z,
        min: -10,
        max: 10,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleWavesCenterChange('z', nextValue);
        },
      },
      "Min Distance": {
        value: previewModelConfig.wavesMinDistance,
        min: 0,
        max: 1,
        step: 0.01,
        hint: "Minimum distance from center (0-1, as fraction of model size)",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesMinDistance', nextValue);
        },
      },
      "Amplitude": {
        value: previewModelConfig.wavesAmplitude,
        min: 1,
        max: 10,
        step: 0.1,
        hint: "Wave width (1-10)",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesAmplitude', nextValue);
        },
      },
      "Period": {
        value: previewModelConfig.wavesPeriod,
        min: 1,
        max: 10,
        step: 0.1,
        hint: "Number of simultaneous waves",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesPeriod', nextValue);
        },
      },
      "Speed": {
        value: previewModelConfig.wavesSpeed,
        min: 0.1,
        max: 5,
        step: 0.1,
        hint: "Wave propagation speed",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesSpeed', nextValue);
        },
      },
      "Color Intensity": {
        value: previewModelConfig.wavesColorIntensity,
        min: 0,
        max: 10,
        step: 0.1,
        hint: "Wave color intensity (0-10)",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesColorIntensity', nextValue);
        },
      },
      "Displacement": {
        value: previewModelConfig.wavesDisplacement,
        min: 0,
        max: 0.1,
        step: 0.001,
        hint: "Particle displacement (0-0.1)",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesDisplacement', nextValue);
        },
      },
    }),
    [previewModelConfig, handleWavesCenterChange, handlePreviewWavesChange]
  );
  useControls(
    "Teller Assistant / Transform",
    () => ({
      "Rotation Y": {
        value: tellerAssistantConfig.transform.rotationY,
        min: -Math.PI * 2,
        max: Math.PI * 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantTransformChange('rotationY', nextValue);
        },
      },
    }),
    [tellerAssistantConfig.transform.rotationY, handleTellerAssistantTransformChange]
  );

  useControls(
    "Teller Assistant / Desktop",
    () => ({
      Scale: {
        value: tellerAssistantConfig.transform.desktop.scale,
        min: 0.0001,
        max: 0.01,
        step: 0.0001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantDesktopChange('scale', nextValue);
        },
      },
      "Hover Amplitude": {
        value: tellerAssistantConfig.transform.desktop.hoverAmplitude,
        min: 0,
        max: 0.02,
        step: 0.0001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantDesktopChange('hoverAmplitude', nextValue);
        },
      },
      "Padding Right (px)": {
        value: tellerAssistantConfig.transform.desktop.paddingRight,
        min: 0,
        max: 500,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantDesktopChange('paddingRight', nextValue);
        },
      },
      "Vertical Offset (px)": {
        value: tellerAssistantConfig.transform.desktop.verticalOffset,
        min: -300,
        max: 300,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantDesktopChange('verticalOffset', nextValue);
        },
      },
    }),
    [tellerAssistantConfig.transform.desktop, handleTellerAssistantDesktopChange]
  );

  useControls(
    "Teller Assistant / Mobile",
    () => ({
      Scale: {
        value: tellerAssistantConfig.transform.mobile.scale,
        min: 0.0001,
        max: 0.01,
        step: 0.0001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMobileChange('scale', nextValue);
        },
      },
      "Hover Amplitude": {
        value: tellerAssistantConfig.transform.mobile.hoverAmplitude,
        min: 0,
        max: 0.02,
        step: 0.0001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMobileChange('hoverAmplitude', nextValue);
        },
      },
      "Padding Right (px)": {
        value: tellerAssistantConfig.transform.mobile.paddingRight,
        min: 0,
        max: 500,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMobileChange('paddingRight', nextValue);
        },
      },
      "Vertical Offset (px)": {
        value: tellerAssistantConfig.transform.mobile.verticalOffset,
        min: -300,
        max: 300,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMobileChange('verticalOffset', nextValue);
        },
      },
    }),
    [tellerAssistantConfig.transform.mobile, handleTellerAssistantMobileChange]
  );

  useControls(
    "Teller Assistant / Mesh",
    () => ({
      "Goo Scale": {
        value: tellerAssistantConfig.mesh.gooScale,
        min: 0,
        max: 50,
        step: 0.1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('gooScale', nextValue);
        },
      },
      "Goo Amount": {
        value: tellerAssistantConfig.mesh.gooAmount,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('gooAmount', nextValue);
        },
      },
      "Goo Speed": {
        value: tellerAssistantConfig.mesh.gooSpeed,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('gooSpeed', nextValue);
        },
      },
      "Goo Pole Amount": {
        value: tellerAssistantConfig.mesh.gooPoleAmount,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('gooPoleAmount', nextValue);
        },
      },
      "Wave Scale": {
        value: tellerAssistantConfig.mesh.waveScale,
        min: 0,
        max: 5,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('waveScale', nextValue);
        },
      },
      "Wave Frequency": {
        value: tellerAssistantConfig.mesh.waveFrequency,
        min: 0,
        max: 10,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('waveFrequency', nextValue);
        },
      },
      "Wave Amount": {
        value: tellerAssistantConfig.mesh.waveAmount,
        min: 0,
        max: 5,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('waveAmount', nextValue);
        },
      },
      "Wave Speed": {
        value: tellerAssistantConfig.mesh.waveSpeed,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('waveSpeed', nextValue);
        },
      },
      "Wave Pole Amount": {
        value: tellerAssistantConfig.mesh.wavePoleAmount,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('wavePoleAmount', nextValue);
        },
      },
      Color: {
        value: tellerAssistantConfig.mesh.color,
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('color', nextValue);
        },
      },
      "Rotation Speed": {
        value: tellerAssistantConfig.mesh.rotationSpeed,
        min: 0,
        max: 0.01,
        step: 0.0001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('rotationSpeed', nextValue);
        },
      },
      "Show Normals": {
        value: tellerAssistantConfig.mesh.showNormals,
        onChange: (nextValue: boolean, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('showNormals', nextValue);
        },
      },
    }),
    [tellerAssistantConfig.mesh, handleTellerAssistantMeshChange]
  );

  useControls(
    "Teller Assistant / Shell",
    () => ({
      "Particles Count": {
        value: tellerAssistantConfig.shell.particlesCount,
        min: 1000,
        max: 50000,
        step: 1000,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesCount', nextValue);
        },
      },
      "Particles Color": {
        value: tellerAssistantConfig.shell.particlesColor,
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesColor', nextValue);
        },
      },
      "Particles Size": {
        value: tellerAssistantConfig.shell.particlesSize,
        min: 0,
        max: 0.1,
        step: 0.001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesSize', nextValue);
        },
      },
      "Particles Radius": {
        value: tellerAssistantConfig.shell.particlesRadius,
        min: 0.5,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesRadius', nextValue);
        },
      },
      "Particles Noise Scale": {
        value: tellerAssistantConfig.shell.particlesNoiseScale,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesNoiseScale', nextValue);
        },
      },
      "Particles Noise Speed": {
        value: tellerAssistantConfig.shell.particlesNoiseSpeed,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesNoiseSpeed', nextValue);
        },
      },
      "Particles Noise Amount": {
        value: tellerAssistantConfig.shell.particlesNoiseAmount,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesNoiseAmount', nextValue);
        },
      },
    }),
    [tellerAssistantConfig.shell, handleTellerAssistantShellChange]
  );

  useControls(
    "Teller Assistant / Lighting",
    // @ts-expect-error Leva schema is wider than the inferred type
    () => {
      const isSimple = tellerAssistantConfig.lighting.lightingType === 'simple';
      
      if (isSimple) {
        return {
          "Lighting Type": {
            value: tellerAssistantConfig.lighting.lightingType,
            options: {
              Simple: "simple",
              Environment: "env",
            },
            onChange: (nextValue: string, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantLightingTypeChange(nextValue as 'simple' | 'env');
            },
          },
          "Light X": {
            value: tellerAssistantConfig.lighting.simple.lightX,
            min: -5,
            max: 5,
            step: 0.1,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightChange('lightX', nextValue);
            },
          },
          "Light Y": {
            value: tellerAssistantConfig.lighting.simple.lightY,
            min: -5,
            max: 5,
            step: 0.1,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightChange('lightY', nextValue);
            },
          },
          "Light Z": {
            value: tellerAssistantConfig.lighting.simple.lightZ,
            min: -5,
            max: 5,
            step: 0.1,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightChange('lightZ', nextValue);
            },
          },
          "Ambient Light Color": {
            value: tellerAssistantConfig.lighting.simple.ambientLightColor,
            onChange: (nextValue: string, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightColorChange('ambientLightColor', nextValue);
            },
          },
          "Directional Light Color": {
            value: tellerAssistantConfig.lighting.simple.directionalLightColor,
            onChange: (nextValue: string, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightColorChange('directionalLightColor', nextValue);
            },
          },
          "Ambient Light Intensity": {
            value: tellerAssistantConfig.lighting.simple.ambientLightIntensity,
            min: 0,
            max: 2,
            step: 0.01,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightIntensityChange('ambientLightIntensity', nextValue);
            },
          },
          "Directional Light Intensity": {
            value: tellerAssistantConfig.lighting.simple.directionalLightIntensity,
            min: 0,
            max: 5,
            step: 0.01,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightIntensityChange('directionalLightIntensity', nextValue);
            },
          },
        };
      } else {
        return {
          "Lighting Type": {
            value: tellerAssistantConfig.lighting.lightingType,
            options: {
              Simple: "simple",
              Environment: "env",
            },
            onChange: (nextValue: string, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantLightingTypeChange(nextValue as 'simple' | 'env');
            },
          },
          "Environment Intensity": {
            value: tellerAssistantConfig.lighting.env.environmentLightingIntensity,
            min: 0,
            max: 20,
            step: 0.1,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantEnvLightChange('environmentLightingIntensity', nextValue);
            },
          },
          "Environment Rotation Y": {
            value: tellerAssistantConfig.lighting.env.environmentRotationY,
            min: -Math.PI,
            max: Math.PI,
            step: 0.01,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantEnvLightChange('environmentRotationY', nextValue);
            },
          },
        };
      }
    },
    [tellerAssistantConfig.lighting.lightingType, tellerAssistantConfig.lighting.simple.lightX, tellerAssistantConfig.lighting.simple.lightY, tellerAssistantConfig.lighting.simple.lightZ, tellerAssistantConfig.lighting.simple.ambientLightColor, tellerAssistantConfig.lighting.simple.directionalLightColor, tellerAssistantConfig.lighting.simple.ambientLightIntensity, tellerAssistantConfig.lighting.simple.directionalLightIntensity, tellerAssistantConfig.lighting.env.environmentLightingIntensity, tellerAssistantConfig.lighting.env.environmentRotationY, handleTellerAssistantLightingTypeChange, handleTellerAssistantSimpleLightChange, handleTellerAssistantSimpleLightColorChange, handleTellerAssistantSimpleLightIntensityChange, handleTellerAssistantEnvLightChange]
  );

  const [, markerSet] = useControls('Markers', buildMarkerSchema, markerSchemaDeps);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  captureMarkerSet(markerSet as any);

  useControls(
    "App Config",
    () => ({
      "Reset to Defaults": button(() => resetConfig()),
      "Copy App Config": button(() => handleCopyAppConfig()),
      "Clear Storage & Reload": button(() => {
        localStorage.clear();
        window.location.reload();
      }),
    }),
    [resetConfig, handleCopyAppConfig]
  );

  return null;
}
